import _ from 'lodash'
import * as React from 'react'
import * as service from '../../services'
import { useNavigate, useLocation } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { Select, FormControl, InputLabel, Box, MenuItem } from '@mui/material'
import { show, codeTree } from '../../redux/slices/Drawer/drawerRight'
import { useTheme } from '@mui/material/styles'
import { FilterAlt } from '@mui/icons-material'
import SearchMain from './SearchMain'
import BottonCustom from '../BottonCustom'
import AlertCustom from '../Alerts'
import LoadingModal from '../LoadingModal'
import { detectCityFromURL } from '../../utils/cityDetector'
import { buildCantonUrl } from '../../utils/cantonUrl'
import { useCities } from '../../context/CitiesContext'

import {
  setSearchByType,
  setSelectedList,
  setFilterState,
  setFiltersUsed,
  setTypeSearchUsed,
  filterListInitial,
  setAdvancedSearchUsed,
  setFilteredData,
  setLastFilterApplied,
  setEnabledFilter,
} from '../../redux/slices/Search/input'

const WIDTHF1 = 200

const SimpleSearch = ({
  open,
  advancedSearch = false,
  basicList,
  windowWidth,
  isAuth,
}) => {
  let navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()
  const alertRef = React.useRef()
  const theme = useTheme()

  const [enableFilterButton, setEnableFilterButton] = React.useState(false)
  const [initialize, setInitialize] = React.useState(false)
  const [loading, setLoading] = React.useState(false)

  const { filter } = useSelector(state => state.filterSearch)
  const { value: isOpenDrawerRight } = useSelector(state => state.drawerRight)
  const citySlug = detectCityFromURL(location.pathname)
  const { getEnabledCantones } = useCities()
  const cantones = getEnabledCantones()

  const WINDOWWIDTH = windowWidth < 600

  const handleChangeOptions = e => {
    dispatch(setSelectedList([]))
    dispatch(setSearchByType(e.target.value))
  }

  const handleFilters = () => {
    if (isOpenDrawerRight) {
      dispatch(show(false))
      dispatch(codeTree(null))
      navigate(buildCantonUrl(citySlug, cantones))
    }

    setLoading(true)

    const fetchWithTimeout = Promise.race([
      service.getAllDataByTreesFilters(basicList.type, basicList.filterList1, false, citySlug),
      new Promise((_, reject) => setTimeout(() => reject(new Error('TIMEOUT')), 10000)),
    ])

    fetchWithTimeout
      .then(geojson => {
        setLoading(false)

        if (!Object.keys(geojson)?.length) {
          alertRef?.current?.message(
            'Registros no encontrados. Intente buscar nuevamente.'
          )
          alertRef?.current?.isSeverity('warning')
          alertRef?.current?.openAlert(true)
          return
        }

        // ✅ CAMBIO PRINCIPAL: Usar setFilteredData en lugar de setFeature
        dispatch(setFilteredData(geojson))
        dispatch(setFilterState(true))
        dispatch(setFiltersUsed(basicList.filterList1))
        dispatch(setTypeSearchUsed(basicList.type))
        dispatch(setAdvancedSearchUsed(advancedSearch))

        // ✅ NUEVO: Guardar información del filtro aplicado para búsquedas básicas
        dispatch(
          setLastFilterApplied([
            {
              type: basicList.type,
              filterList1: basicList.filterList1,
              searchType: 'basic',
            },
          ])
        )

        dispatch(filterListInitial())
        dispatch(setEnabledFilter(true))
      })
      .catch(err => {
        setLoading(false)
        const isTimeout = err.message === 'TIMEOUT'
        alertRef?.current?.message(
          isTimeout
            ? 'La búsqueda ha tardado demasiado. Intente nuevamente.'
            : 'Ocurrió un error al cargar los datos.'
        )
        alertRef?.current?.openAlert(true)
        console.error('Error simple search:>> ', err)
      })
  }

  React.useEffect(() => {
    if (!filter) return
    const enableBtt = Boolean(basicList.filterList1.length)
    setEnableFilterButton(enableBtt)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter])

  React.useEffect(() => {
    if (!Boolean(basicList.filterList1.length)) return
    setEnableFilterButton(_.isEqual(basicList.filterList1, basicList.usedFilter1))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basicList.filterList1])

  React.useEffect(() => {
    if (!open) return
    setEnableFilterButton(true)
    if (advancedSearch) return
    if (!_.isEqual(basicList.type, basicList.usedType))
      dispatch(setSearchByType(basicList.usedType))
    if (!_.isEqual(basicList.filterList1, basicList.usedFilter1))
      dispatch(setSelectedList(basicList.usedFilter1))
    setInitialize(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  return (
    <Box
      sx={{
        bgcolor: theme.palette.background.default,
        border: `2px solid ${theme.palette.divider}`,
        borderRadius: 1,
        p: 2,
      }}>
      <Box
        sx={{
          pb: 2,
          width: '100%',
          minWidth: 'max(40vw, 150px)',
          maxWidth: 'calc(100vw - 95px)',
          display: 'flex',
          flexDirection: WINDOWWIDTH ? 'column' : 'row',
        }}>
        <LoadingModal open={loading} />
        <FormControl
          size="small"
          sx={{
            width: !Boolean(basicList.type) ? '100%' : WINDOWWIDTH ? '100%' : WIDTHF1,
            pb: Boolean(basicList.type) && WINDOWWIDTH ? 1 : 0,
            pr: Boolean(basicList.type) && !WINDOWWIDTH ? 1 : 0,
          }}>
          <InputLabel
            id="simple-search-label"
            sx={{
              fontSize: {
                xs: '0.85rem', // pantallas pequeñas (<600px)
                sm: '0.95rem',
                md: '1.rem',
              },
            }}>
            Buscar por ...
          </InputLabel>
          <Select
            labelId="simple-search-label"
            id="select-simple"
            value={basicList.type}
            name="selectSimpleBox"
            label="Buscar por ..."
            onChange={handleChangeOptions}>
            <MenuItem value="specie">Nombre Científico</MenuItem>
            <MenuItem value="pseudonym">Nombre Común</MenuItem>
            <MenuItem value="family">Familia</MenuItem>
            {isAuth && <MenuItem value="code">Código</MenuItem>}
          </Select>
        </FormControl>

        {initialize && (
          <SearchMain
            searchType={basicList.type}
            filterList1={basicList.filterList1}
            width={WINDOWWIDTH ? '100%' : `max(${WIDTHF1}px, 40vw)`}
          />
        )}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Box sx={{ ml: 1 }}>
          <BottonCustom
            variant="contained"
            text="Filtrar"
            onClick={handleFilters}
            startIcon={<FilterAlt />}
            disabled={!Boolean(basicList.filterList1.length) || enableFilterButton}
            styleButton={(theme, props) => ({
              color: theme.palette.primary.main,
              border: `1px solid ${
                props.disabled ? theme.palette.divider : theme.palette.primary.main
              }`,
              backgroundColor: !props.disabled
                ? 'transparent'
                : theme.palette.primary.main,
              width: 'auto',
              fontSize: '12px',
              '&:hover': {
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.background.default,
              },
              '&:active': { boxShadow: 'none', opacity: 0.9 },
            })}
            size="small"
          />
        </Box>
        <AlertCustom ref={alertRef} severity="error" />
      </Box>
    </Box>
  )
}
export default SimpleSearch
